import React, { useEffect, useMemo, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";

function itemActive(pathname, to, siblingTos) {
  if (to === "/") return pathname === "/";
  if (pathname === to) return true;
  if (pathname.startsWith(`${to}/`)) {
    const longerSibling = siblingTos.some(
      (other) => other !== to && other.startsWith(`${to}/`) && (pathname === other || pathname.startsWith(`${other}/`)),
    );
    if (longerSibling) return false;
    return true;
  }
  return false;
}

/**
 * Phone sticky chips for the current sidebar group (e.g. Sales → Contract / Link / Loading…).
 * Hidden on desktop and when the active group has only one item.
 */
export default function MobileSubTabBar({ group }) {
  const { pathname } = useLocation();
  const scrollerRef = useRef(null);
  const items = group?.items || [];
  const siblingTos = useMemo(() => items.map((n) => n.to), [items]);

  useEffect(() => {
    const root = scrollerRef.current;
    if (!root) return;
    const active = root.querySelector("[data-active='true']");
    if (active?.scrollIntoView) {
      active.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
    }
  }, [pathname, group?.id]);

  if (!group || items.length < 2) return null;

  return (
    <div
      className="lg:hidden border-b border-white/10 bg-bansal-ink text-bansal-ivory"
      data-testid="mobile-sub-tab-bar"
    >
      <div className="px-3 pt-1.5 pb-0.5 flex items-center justify-between gap-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-bansal-ivory/45 font-semibold truncate">
          {group.label}
        </p>
        <p className="text-[10px] text-bansal-ivory/40 tabular shrink-0">{items.length}</p>
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-2 overflow-x-auto px-3 pb-2.5 snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        role="tablist"
        aria-label={`${group.label} sub pages`}
      >
        {items.map((item) => {
          const active = itemActive(pathname, item.to, siblingTos);
          const label = item.shortLabel || item.label;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={siblingTos.some((other) => other !== item.to && other.startsWith(`${item.to}/`))}
              data-active={active ? "true" : "false"}
              data-testid={`mobile-sub-tab-${item.to.replace(/\//g, "-").replace(/^-/, "")}`}
              className={`snap-start shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 min-h-10 text-xs font-medium touch-manipulation transition-colors ${
                active
                  ? "bg-bansal-gold text-bansal-ink border-bansal-gold shadow-sm"
                  : "bg-white/10 text-bansal-ivory/85 border-white/15 active:bg-white/15"
              }`}
            >
              {item.icon ? (
                <item.icon className={`h-3.5 w-3.5 shrink-0 ${active ? "opacity-95" : "opacity-70"}`} aria-hidden />
              ) : null}
              <span className="whitespace-nowrap">{label}</span>
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}

/** Find which nav group owns the current route. */
export function findActiveNavGroup(groups, pathname) {
  if (!groups?.length) return null;
  for (const group of groups) {
    if ((group.items || []).some((item) => itemActive(pathname, item.to, group.items.map((n) => n.to)))) {
      return group;
    }
  }
  return null;
}
